import { TypeLang } from '@/schema'
import { LANG, MAIN_NAVIGATION_TRANSLATION } from '@/utils'
import { useParams } from 'next/navigation'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import { Img, Link } from '../base'
import { MODALS } from '@/context'
import { defaultGrid, designGrid, innerGrid, mq } from '@/styles'
import { useModal, useTranslator } from '@/hooks'
import { LangDropdown } from './LangDropdown'

const SVG_LOGO_VADEVER = {
  alt: 'Vadever Logotype',
  type: 'svg' as const,
  src: '/images/svg/logo--vadever--white.svg',
  size: { width: 183, height: 33 },
  content_type: 'image/svg+xml'
}

const SVG_CONTACT_BUBBLES = {
  alt: 'Contact icon',
  type: 'svg' as const,
  src: '/images/svg/i--contact-bubbles.svg',
  size: { width: 28, height: 27 },
  content_type: 'image/svg+xml'
}


type TypeInnerLink = { title: string; id: string; }

const onClickInnerLink = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
  if (!id) return
  // prevent default link behavior
  e.preventDefault()
  // smooth scroll to section
  const element = document.getElementById(id.substring(1)) // remove the '#' from the id
  // Add 80px offset for fixed navbar
  if (element) {
    const offsetTop = element.getBoundingClientRect().top + window.scrollY - 80
    window.scrollTo({ top: offsetTop, behavior: 'smooth' })
  }
}

const getInnerLinks = (arr: TypeInnerLink[]) => arr.map((item, idx) => (
  <li key={idx}>
    <Link href={item?.id} onClick={(e) => onClickInnerLink(e, item?.id)}>
      {item.title}
    </Link>
  </li>
))

export const Navbar:FC = () => {
  const { lang } = useParams() as { lang: TypeLang }
  const { toggle } = useModal(MODALS.MENU)
  const { toggle:toggleContact } = useModal(MODALS.CONTACT)
  const { t } = useTranslator()


  // eslint-disable-next-line react-hooks/exhaustive-deps
  const Items = useMemo(() => getInnerLinks(t('project.internal_page_links') as unknown as TypeInnerLink[]), [t, lang])


  const onClickMenu = () => {
    toggle()
  }

  const onClickContact = () => {
    toggleContact()
  }

  return (
    <Nav aria-label={MAIN_NAVIGATION_TRANSLATION[lang ?? LANG]}>
      <div>
        <button type='button' aria-haspopup='dialog' className='btn btn--menu' aria-controls={MODALS.MENU} aria-label='Menu' onClick={onClickMenu}>
          <svg aria-hidden='true' xmlns='http://www.w3.org/2000/svg' width='22' height='17' viewBox='0 0 22 17'><path d='M.68 1.58h21M.68 8.5h21m-21 6.92h21' stroke='var(--color-text)' strokeWidth='2.33' fill='none' strokeLinejoin='round' /></svg>
        </button>
        <ul className='ul--links'>
          {Items}
          {/* <li><a href='https://vadever.com' target='_blank' rel='noopener noreferrer'>Studio</a></li> */}
        </ul>
        <Link href='/' className='logo'>
          <Img media={SVG_LOGO_VADEVER} />
          <span className='bubble--reels'>REELS</span>
        </Link>
        <LangDropdown />
        <button className='btn btn--contact' onClick={onClickContact}>
          <Img media={SVG_CONTACT_BUBBLES} />
          <span>{t('contact_us')}</span>
        </button>
      </div>
    </Nav>
  )
}

const Nav = styled.nav`
  background-color: var(--color-background);
  ${designGrid({})}
  position: fixed;
  top: 0;
  width: 100vw;
  width: 100dvw;
  transition: 250ms background-color ease-in-out;
  z-index: 10;

  ${mq.greaterThan('tablet')} {
    background-color: transparent;
  }

  > div {
    align-items: center;
    display: flex;
    height: 75px;
    grid-column: 1 / span 6;
    justify-content: space-between;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
      justify-content: center;
      ${innerGrid(defaultGrid.tablet)}
    }

    ${mq.greaterThan('desktop')} {
      ${innerGrid(defaultGrid.desktop)}
    }
  }

  .dropdown--lang {
    grid-column: 10 / span 1;
    margin-left: auto;

    ul {
      background-color: var(--color-background);
      color: var(--color-text);
    }

    ${mq.lessThan('tablet')} {
      display: none;
    }
  }

  /* Logo Animation */
  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  button {
    animation: fadeIn 1500ms ease-out 50ms forwards;
    align-items: center;
    display: flex;
    justify-content: center;
    opacity: 0;

    &.btn--menu {
      ${mq.greaterThan('desktop-md')} {
        display: none;
      }
    }

    &.btn--contact {
      align-items: center;
      background-color: var(--color-gold);
      border-radius: 100px;
      border: 2px solid var(--color-gold);
      color: var(--color-black);
      display: flex;
      justify-content: center;
      padding: 14px;
      transition: 250ms background-color ease-in-out, 250ms color ease-in-out;
      width: fit-content;

      ${mq.greaterThan('tablet')} {
        flex-direction: column;
        grid-column: 11 / span 2;
        margin-left: auto;
        padding: 20px;
      }

      &:hover {
        background-color: var(--color-black);
        color: var(--color-gold);
      }

      span {
        font-size: 15px;
        font-weight: 500;

        ${mq.lessThan('tablet')} {
          display: none;
        }
      }

      img {
        width: 26px;

        ${mq.greaterThan('tablet')} {
          display: none;
        }
      }
    }
  }

  a {
    align-items: center;
    display: flex;
    justify-content: center;

    /* Logo Animation */
    @keyframes logo-animation {
      0% {
        opacity: 0;
        transform: translateY(-100%);
      }
      100% {
        opacity: 1;
        transform: translateY(-2px);
      }
    }

    &.logo {
      animation: logo-animation 400ms ease-out 50ms forwards;
      position: relative;
      transform: translateY(-2px);
      width: 142px;

      ${mq.greaterThan('tablet')} {
        grid-column: 6 / span 2;
        width: 182px;
      }

      img {
        width: 100%;
      }
    }

    /* Bubble Reels Animation */
    @keyframes bubble-reels-animation {
      0% {
        transform: scale(0) rotate(-45deg);
      }
      20% {
        transform: scale(1) rotate(-4.911deg);
      }
      40% { transform: scale(0.9, 1.1) rotate(-4.911deg); }
      60% { transform: scale(1.1, 0.9) rotate(-4.911deg); }
      80% { transform: scale(0.95, 1.05) rotate(-4.911deg); }
      100% { transform: scale(1) rotate(-4.911deg); }
    }

    .bubble--reels {
      align-items: center;
      background-color: var(--color-gold);
      border-radius: 21px;
      color: var(--color-black);
      display: flex;
      font-size: 12.471px;
      flex-shrink: 0;
      height: 25px;
      justify-content: center;
      left: 120px;
      line-height: 80%;
      padding: 8.908px 8.908px 8.314px 8.908px;
      position: absolute;
      top: 18px;
      transform: scale(0) rotate(-45deg);
      transition: transform 250ms ease-in-out;
      animation: bubble-reels-animation 650ms ease-out 450ms forwards;
      width: 57.01px;

      ${mq.greaterThan('tablet')} {
        left: 155px;
        padding: 9px 9px 8px 9px;
        top: 23px;
      }
    }
  }

  .ul--links {
    display: none;

    ${mq.greaterThan('desktop-md')} {
      align-items: center;
      display: flex;
      flex-direction: row;
      gap: 30px;
      grid-column: 1 / span 5;
      justify-content: flex-start;
      list-style: none;
    }

    a {
      font-size: 13px;
      font-style: normal;
      font-weight: 500;
      letter-spacing: 0.65px;
      line-height: 100%; /* 13px */
      opacity: .4;
      text-transform: uppercase;
      transition: opacity 250ms ease-in-out;
      white-space: nowrap;

      &.ac {
        opacity: 1;
      }

      &:hover {
        opacity: .8;
      }
    }
  }
`
